import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { 
  FileText, 
  DollarSign, 
  Search,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Calendar,
  Download,
  Upload,
  ArrowRightLeft,
  Minus,
  X as CloseIcon,
  Edit,
  Eye
} from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Current admin info
  const [currentAdmin, setCurrentAdmin] = useState(null);
  
  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTransaction, setEditTransaction] = useState(null);
  const [editForm, setEditForm] = useState({
    amount: '',
    notes: ''
  });
  
  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailTransaction, setDetailTransaction] = useState(null);

  useEffect(() => {
    // Get current admin info from API
    const fetchCurrentAdmin = async () => {
      const token = localStorage.getItem('admin_token');
      try {
        const response = await axios.get(`${API}/api/admin/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentAdmin(response.data);
      } catch (error) {
        console.error('Failed to fetch current admin:', error);
      }
    };
    
    fetchCurrentAdmin();
    fetchTransactions();
  }, [typeFilter, statusFilter]);

  const fetchTransactions = async () => {
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    
    try {
      const params = {};
      if (typeFilter !== 'all') params.action_type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await axios.get(`${API}/api/admin/my-transactions`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      console.log('📊 Transactions data:', response.data.transactions);
      console.log('👤 Current admin:', currentAdmin);
      
      setTransactions(response.data.transactions || []);
      setFilteredTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      toast.error('Gagal memuat transaksi');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTransaction = async (transactionId) => {
    if (!window.confirm('Apakah Anda yakin ingin membatalkan transaksi ini?')) {
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    
    try {
      await axios.put(
        `${API}/api/admin/my-transactions/${transactionId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Transaksi berhasil dibatalkan');
      fetchTransactions();
    } catch (error) {
      console.error('Failed to cancel transaction:', error);
      toast.error(error.response?.data?.detail || 'Gagal membatalkan transaksi');
    }
  };

  const handleOpenEditModal = (transaction) => {
    setEditTransaction(transaction);
    setEditForm({
      amount: transaction.amount,
      notes: transaction.notes || ''
    });
    setShowEditModal(true);
  };
  
  const handleOpenDetailModal = (transaction) => {
    setDetailTransaction(transaction);
    setShowDetailModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('admin_token');
    
    try {
      await axios.put(
        `${API}/api/admin/my-transactions/${editTransaction.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Transaksi berhasil diupdate');
      setShowEditModal(false);
      fetchTransactions();
    } catch (error) {
      console.error('Failed to update transaction:', error);
      toast.error(error.response?.data?.detail || 'Gagal mengupdate transaksi');
    }
  };

  useEffect(() => {
    let filtered = transactions;
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.client_username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredTransactions(filtered);
    setCurrentPage(1);
  }, [searchTerm, transactions]);

  const getTypeLabel = (type) => {
    const labels = {
      topup_wallet: 'Top-Up Wallet',
      withdraw_account: 'Withdraw',
      transfer_wallet_to_account: 'Transfer',
      deduct_wallet: 'Kurangi Saldo'
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'topup_wallet': return <Upload className="h-4 w-4 text-green-600" />;
      case 'withdraw_account': return <Download className="h-4 w-4 text-red-600" />;
      case 'transfer_wallet_to_account': return <ArrowRightLeft className="h-4 w-4 text-blue-600" />;
      case 'deduct_wallet': return <Minus className="h-4 w-4 text-orange-600" />;
      default: return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <Clock className="h-3 w-3" />, label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle className="h-3 w-3" />, label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: <XCircle className="h-3 w-3" />, label: 'Rejected' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', icon: <CloseIcon className="h-3 w-3" />, label: 'Cancelled' }
    };
    
    const statusConfig = config[status] || config.pending;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
        {statusConfig.icon}
        {statusConfig.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatWalletType = (walletType) => {
    if (!walletType) return '-';
    
    // Remove "wallet_" prefix and convert to uppercase
    const cleaned = walletType.replace(/wallet_/gi, '').toUpperCase();
    
    // Map common wallet types to user-friendly names
    const walletNames = {
      'MAIN_IDR': 'Wallet Utama IDR',
      'MAIN_USD': 'Wallet Utama USD',
      'IDR': 'Wallet IDR',
      'USD': 'Wallet USD',
      'SECONDARY_IDR': 'Wallet Sekunder IDR',
      'SECONDARY_USD': 'Wallet Sekunder USD'
    };
    
    return walletNames[cleaned] || `Wallet ${cleaned}`;
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="h-8 w-8 text-blue-600" />
          Transaksi Admin
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Semua transaksi yang dibuat oleh admin
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Search className="h-4 w-4 inline mr-1" />
              Cari Client
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cari berdasarkan nama client atau ID..."
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="h-4 w-4 inline mr-1" />
              Tipe Transaksi
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Semua Tipe</option>
              <option value="topup_wallet">Top-Up Wallet</option>
              <option value="withdraw_account">Withdraw</option>
              <option value="transfer_wallet_to_account">Transfer</option>
              <option value="deduct_wallet">Kurangi Saldo</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="h-4 w-4 inline mr-1" />
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Semua Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Memuat transaksi...</p>
          </div>
        ) : currentTransactions.length === 0 ? (
          <div className="p-8 text-center">
            <AlertTriangle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Tidak ada transaksi ditemukan</p>
          </div>
        ) : (
          <>
            {/* Desktop View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipe</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approved By</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentTransactions.map((transaction) => {
                    const isOwnTransaction = currentAdmin && transaction.admin_id === currentAdmin.id;
                    
                    // Debug logs
                    if (transaction.status === 'pending') {
                      console.log('🔍 Pending transaction:', {
                        id: transaction.id,
                        admin_id: transaction.admin_id,
                        currentAdmin_id: currentAdmin?.id,
                        isOwnTransaction
                      });
                    }
                    
                    if (transaction.approved_by) {
                      console.log('✅ Approved by:', transaction.approved_by);
                    }
                    
                    return (
                      <tr key={transaction.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900">{transaction.client_username}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-xs">
                            <p className="font-medium text-gray-900">{transaction.admin_username}</p>
                            {isOwnTransaction && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Saya
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getTypeIcon(transaction.type)}
                            <span className="text-sm text-gray-900">{getTypeLabel(transaction.type)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-bold text-gray-900">
                            {formatCurrency(transaction.amount, transaction.currency)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          {getStatusBadge(transaction.status)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(transaction.created_at)}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {transaction.approved_by ? (
                            <div className="text-xs">
                              <p className="font-medium text-gray-900">{transaction.approved_by.name}</p>
                              <p className="text-gray-500">{formatDate(transaction.approved_at)}</p>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-2">
                            {/* Detail Button - Always visible for all admins */}
                            <button
                              onClick={() => handleOpenDetailModal(transaction)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              Detail
                            </button>
                            
                            {/* Edit Button - Only for pending transactions */}
                            {transaction.status === 'pending' && (
                              <button
                                onClick={() => isOwnTransaction && handleOpenEditModal(transaction)}
                                disabled={!isOwnTransaction}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-medium rounded-lg shadow-sm transition-all duration-200 ${
                                  isOwnTransaction
                                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 hover:shadow-md cursor-pointer'
                                    : 'bg-gray-400 cursor-not-allowed opacity-60'
                                }`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                                Edit
                              </button>
                            )}
                            
                            {/* Cancel Button - Only for pending transactions */}
                            {transaction.status === 'pending' && (
                              <button
                                onClick={() => isOwnTransaction && handleCancelTransaction(transaction.id)}
                                disabled={!isOwnTransaction}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-medium rounded-lg shadow-sm transition-all duration-200 ${
                                  isOwnTransaction
                                    ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 hover:shadow-md cursor-pointer'
                                    : 'bg-gray-400 cursor-not-allowed opacity-60'
                                }`}
                              >
                                <XCircle className="h-3.5 w-3.5" />
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="md:hidden divide-y divide-gray-200">
              {currentTransactions.map((transaction) => {
                const isOwnTransaction = currentAdmin && transaction.admin_id === currentAdmin.id;
                
                return (
                  <div key={transaction.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {getTypeIcon(transaction.type)}
                          <span className="text-sm font-medium text-gray-900">{getTypeLabel(transaction.type)}</span>
                        </div>
                        <p className="text-xs text-gray-600">Client: {transaction.client_username}</p>
                        <p className="text-xs text-gray-600">
                          Admin: <span className="font-medium">{transaction.admin_username}</span>
                          {isOwnTransaction && (
                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Saya
                            </span>
                          )}
                        </p>
                      </div>
                      {getStatusBadge(transaction.status)}
                    </div>
                    
                    <div className="space-y-1 mb-2">
                      <p className="text-sm font-bold text-gray-900">
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </p>
                      <p className="text-xs text-gray-600">{formatDate(transaction.created_at)}</p>
                      {transaction.approved_by && (
                        <p className="text-xs text-gray-600">
                          Approved by: <span className="font-medium">{transaction.approved_by.name}</span>
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 mt-2">
                      {/* Detail Button - Always visible */}
                      <button
                        onClick={() => handleOpenDetailModal(transaction)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        <Eye className="h-4 w-4" />
                        Detail
                      </button>
                      
                      {/* Edit Button - Only for pending */}
                      {transaction.status === 'pending' && (
                        <button
                          onClick={() => isOwnTransaction && handleOpenEditModal(transaction)}
                          disabled={!isOwnTransaction}
                          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-white text-sm font-medium rounded-lg shadow-sm transition-all duration-200 ${
                            isOwnTransaction
                              ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 hover:shadow-md cursor-pointer'
                              : 'bg-gray-400 cursor-not-allowed opacity-60'
                          }`}
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </button>
                      )}
                      
                      {/* Cancel Button - Only for pending */}
                      {transaction.status === 'pending' && (
                        <button
                          onClick={() => isOwnTransaction && handleCancelTransaction(transaction.id)}
                          disabled={!isOwnTransaction}
                          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-white text-sm font-medium rounded-lg shadow-sm transition-all duration-200 ${
                            isOwnTransaction
                              ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 hover:shadow-md cursor-pointer'
                              : 'bg-gray-400 cursor-not-allowed opacity-60'
                          }`}
                        >
                          <XCircle className="h-4 w-4" />
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {filteredTransactions.length > itemsPerPage && (
              <div className="px-4 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Tampilkan:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-md"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-sm text-gray-700">
                      | {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredTransactions.length)} dari {filteredTransactions.length}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                    >
                      Sebelumnya
                    </button>
                    <span className="text-sm text-gray-700">
                      Hal {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                    >
                      Selanjutnya
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Edit Transaksi</h3>
            </div>
            
            <form onSubmit={handleEditSubmit}>
              <div className="px-6 py-4 space-y-4">
                {/* Transaction Info */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">Client: <span className="font-medium text-gray-900">{editTransaction.client_username}</span></p>
                  <p className="text-sm text-gray-600">Tipe: <span className="font-medium text-gray-900">{getTypeLabel(editTransaction.type)}</span></p>
                  <p className="text-sm text-gray-600">Currency: <span className="font-medium text-gray-900">{editTransaction.currency}</span></p>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    min="0"
                    step="0.01"
                  />
                  {/* Currency Format Preview */}
                  {editForm.amount && (
                    <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 font-medium mb-1">Format Preview:</p>
                      <p className="text-lg font-bold text-blue-900">
                        {formatCurrency(parseFloat(editForm.amount) || 0, editTransaction.currency)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catatan
                  </label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Tambahkan catatan (opsional)"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && detailTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-gray-900">Detail Transaksi</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              {/* Transaction ID */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <p className="text-xs text-blue-600 font-medium mb-1">Transaction ID</p>
                <p className="text-sm font-mono text-gray-900 break-all">{detailTransaction.id}</p>
              </div>

              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Status:</span>
                {getStatusBadge(detailTransaction.status)}
              </div>

              {/* Transaction Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Tipe Transaksi</p>
                  <div className="flex items-center gap-2">
                    {getTypeIcon(detailTransaction.type)}
                    <span className="text-sm font-medium text-gray-900">{getTypeLabel(detailTransaction.type)}</span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Currency</p>
                  <p className="text-sm font-medium text-gray-900">{detailTransaction.currency}</p>
                </div>
              </div>

              {/* Amount */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                <p className="text-xs text-green-600 font-medium mb-1">Amount</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(detailTransaction.amount, detailTransaction.currency)}
                </p>
              </div>

              {/* Transaction Details - Specific to transaction type */}
              {detailTransaction.details && (
                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    Detail Transaksi
                  </h4>
                  <div className="space-y-2">
                    {/* Transfer Wallet to Account Details */}
                    {detailTransaction.type === 'transfer_wallet_to_account' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Dari Wallet:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {formatWalletType(detailTransaction.details.wallet_type)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Ke Akun:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {detailTransaction.details.account_name || '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">ID Akun:</span>
                          <span className="text-sm font-mono text-gray-700">
                            {detailTransaction.details.account_id || '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Platform:</span>
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {detailTransaction.details.platform || '-'}
                          </span>
                        </div>
                      </>
                    )}

                    {/* Withdraw Account Details */}
                    {detailTransaction.type === 'withdraw_account' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Dari Akun:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {detailTransaction.details.account_name || '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">ID Akun:</span>
                          <span className="text-sm font-mono text-gray-700">
                            {detailTransaction.details.account_id || '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Platform:</span>
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {detailTransaction.details.platform || '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Ke Wallet:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {formatWalletType(detailTransaction.details.target_wallet)}
                          </span>
                        </div>
                      </>
                    )}

                    {/* Top Up Wallet Details */}
                    {detailTransaction.type === 'topup_wallet' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Wallet:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {formatWalletType(detailTransaction.details.wallet_type)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Metode Pembayaran:</span>
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {detailTransaction.details.payment_method || '-'}
                          </span>
                        </div>
                        {detailTransaction.details.payment_method === 'bank' && detailTransaction.details.bank_name && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Bank:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {detailTransaction.details.bank_name}
                            </span>
                          </div>
                        )}
                        {detailTransaction.details.payment_method === 'crypto' && detailTransaction.details.wallet_address && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Wallet Address:</span>
                            <span className="text-xs font-mono text-gray-700 break-all">
                              {detailTransaction.details.wallet_address}
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Deduct Wallet Details */}
                    {detailTransaction.type === 'deduct_wallet' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Wallet:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {formatWalletType(detailTransaction.details.wallet_type)}
                          </span>
                        </div>
                        {detailTransaction.details.reason && (
                          <div>
                            <span className="text-sm text-gray-600">Alasan:</span>
                            <p className="text-sm font-medium text-gray-900 mt-1">
                              {detailTransaction.details.reason}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Client Info */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-600" />
                  Informasi Client
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Username:</span>
                    <span className="text-sm font-medium text-gray-900">{detailTransaction.client_username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Client ID:</span>
                    <span className="text-sm font-mono text-gray-700">{detailTransaction.client_id?.substring(0, 8)}...</span>
                  </div>
                </div>
              </div>

              {/* Admin Info */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4 text-purple-600" />
                  Informasi Admin
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Admin Username:</span>
                    <span className="text-sm font-medium text-gray-900">{detailTransaction.admin_username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Admin ID:</span>
                    <span className="text-sm font-mono text-gray-700">{detailTransaction.admin_id?.substring(0, 8)}...</span>
                  </div>
                </div>
              </div>

              {/* Approval Info (if approved/rejected) */}
              {detailTransaction.approved_by && (
                <div className="border border-gray-200 rounded-lg p-4 bg-green-50">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Informasi Approval
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Approved By:</span>
                      <span className="text-sm font-medium text-gray-900">{detailTransaction.approved_by.name}</span>
                    </div>
                    {detailTransaction.approved_at && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Approved At:</span>
                        <span className="text-sm font-medium text-gray-900">{formatDate(detailTransaction.approved_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-600" />
                  Waktu
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Dibuat:</span>
                    <span className="text-sm font-medium text-gray-900">{formatDate(detailTransaction.created_at)}</span>
                  </div>
                  {detailTransaction.approved_at && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Diproses:</span>
                      <span className="text-sm font-medium text-gray-900">{formatDate(detailTransaction.approved_at)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {detailTransaction.notes && (
                <div className="border border-gray-200 rounded-lg p-4 bg-yellow-50">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Catatan</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{detailTransaction.notes}</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end sticky bottom-0 bg-white">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTransactions;
